import { ell } from './common';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: string;
  size: number;
  g: number;
}

interface Popup {
  text: string;
  x: number;
  y: number;
  t: number;
  color: string;
  size: number;
}

/** Short-lived visual effects. Purely cosmetic: never affects the score. */
export class Fx {
  particles: Particle[] = [];
  popups: Popup[] = [];
  ripples: number[] = [];
  shake = 0;
  flash = 0;
  flashColor = '#FFFFFF';
  squash = 0;
  croakT = 9;
  tangleT = -1;
  slideX = 0;
  slideV = 0;
  tongue: { t: number; x: number; h: number; hit: boolean } | null = null;
  bolt: { x: number; t: number; seed: number } | null = null;
  bubblePopT = 9;
  reduced = false;

  reset(): void {
    this.particles.length = 0;
    this.popups.length = 0;
    this.ripples.length = 0;
    this.shake = this.flash = this.squash = 0;
    this.croakT = 9;
    this.tangleT = -1;
    this.slideX = this.slideV = 0;
    this.tongue = null;
    this.bolt = null;
    this.bubblePopT = 9;
  }

  update(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }
      p.vy += p.g * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    for (let i = this.popups.length - 1; i >= 0; i--) {
      this.popups[i].t += dt;
      if (this.popups[i].t > 0.95) this.popups.splice(i, 1);
    }
    for (let i = this.ripples.length - 1; i >= 0; i--) {
      this.ripples[i] += dt;
      if (this.ripples[i] >= 1) this.ripples.splice(i, 1);
    }
    this.shake = Math.max(0, this.shake - dt * 2.5);
    this.flash = Math.max(0, this.flash - dt * 2.2);
    this.squash = Math.max(0, this.squash - dt * 5);
    this.croakT += dt;
    this.bubblePopT += dt;
    if (this.tangleT >= 0) this.tangleT += dt;
    // ice slide: spring back to the middle of the floe
    this.slideV += -this.slideX * 18 * dt;
    this.slideV *= Math.max(0, 1 - 3.5 * dt);
    this.slideX += this.slideV * dt;
    if (this.tongue) {
      this.tongue.t += dt;
      if (this.tongue.t > 0.18) this.tongue = null;
    }
    if (this.bolt) {
      this.bolt.t += dt;
      if (this.bolt.t > 0.35) this.bolt = null;
    }
  }

  burst(x: number, y: number, color: string, n: number, speed = 160, life = 0.6, g = 500, size = 3.5): void {
    if (this.reduced) n = Math.ceil(n / 3);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = speed * (0.4 + Math.random() * 0.8);
      this.particles.push({
        x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - speed * 0.4,
        life: life * (0.6 + Math.random() * 0.6), max: life, color, size: size * (0.6 + Math.random() * 0.7), g,
      });
    }
  }

  popup(text: string, x: number, y: number, color = '#FFE28A', size = 26): void {
    this.popups.push({ text, x, y, t: 0, color, size });
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const p of this.particles) {
      ctx.globalAlpha = Math.min(1, p.life / (p.max * 0.5));
      ell(ctx, p.x, p.y, p.size, p.size);
      ctx.fillStyle = p.color;
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.textAlign = 'center';
    ctx.lineJoin = 'round';
    for (const p of this.popups) {
      const a = 1 - p.t / 0.95;
      const rise = this.reduced ? 0 : p.t * 46;
      ctx.font = `800 ${p.size}px Gluten, "Trebuchet MS", sans-serif`;
      ctx.globalAlpha = Math.max(0, a);
      ctx.lineWidth = 5;
      ctx.strokeStyle = '#173B33';
      ctx.strokeText(p.text, p.x, p.y - rise);
      ctx.fillStyle = p.color;
      ctx.fillText(p.text, p.x, p.y - rise);
    }
    ctx.globalAlpha = 1;
  }
}
